import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import { ACHIEVEMENTS_FONT, achievementsColors, RADIUS_CARD } from './achievementsTheme';
import { achievementText, AchievementView } from './achievementsViewModel';
import ProfileTile, { TILE_HEIGHT } from './ProfileTile';

/**
 * Figma "Selected for Profile" (4488:19649) - the shelf under the grid: a heading with the n / 5
 * counter, five profile slots and a line of guidance. While nothing is selected the slots are
 * covered by "First TXT" (4488:19964), a 30% black plate with the file's all-caps placeholder.
 */
export const SHELF_WIDTH = 334;
export const SHELF_HEIGHT = 162;
const INNER_X = 11;
const INNER_WIDTH = 312;
const HEADING_Y = 11;
const HEADING_HEIGHT = 20;
const TILES_Y = 39;
const FOOTER_Y = 137;
/** A hint that wraps (the longer translations) moves up into the gap under the tiles to fit. */
const FOOTER_WRAPPED_Y = TILES_Y + TILE_HEIGHT + 4;
const FOOTER_LINE = 1.2;
const FOOTER_WRAPPED_LINE = 1.1;
const EMPTY_TEXT_WIDTH = 217;

type Props = {
	selected: Array<AchievementView | undefined>;
	maxSelected: number;
	/** A filled slot opens its achievement... */
	onOpen: (achievement: AchievementView) => void;
	/** ...and only its ✕ takes it off the shelf. */
	onRemove: (achievement: AchievementView) => void;
	scale: number;
};

export const SelectedForProfile = ({ selected, maxSelected, onOpen, onRemove, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const count = selected.filter(Boolean).length;
	const [footerWrapped, setFooterWrapped] = useState(false);

	return (
		<View style={styles.shelf}>
			<View style={styles.heading}>
				<Text numberOfLines={1} style={styles.headingLabel}>
					{achievementText(t, 'T_SITE_ACHIEVEMENTS_SELECTED_FOR_PROFILE')}
				</Text>
				<Text numberOfLines={1} style={styles.headingCount}>
					{count} / {maxSelected}
				</Text>
			</View>

			<View style={styles.tiles}>
				{selected.map((achievement, index) => {
					const label = achievement ? t(achievement.shownTier.nameKey) : '';

					return (
						<ProfileTile
							key={achievement?.familyKey ?? `empty-${index}`}
							item={achievement ? { label, renderArtwork: achievement.renderBadge } : undefined}
							onPress={achievement ? () => onOpen(achievement) : undefined}
							onRemove={achievement ? () => onRemove(achievement) : undefined}
							removeLabel={achievementText(t, 'T_SITE_ACHIEVEMENTS_REMOVE_A11Y', [label])}
							scale={scale}
						/>
					);
				})}
			</View>

			{count === 0 && (
				<View pointerEvents='none' style={styles.emptyPlate}>
					<Text style={styles.emptyText}>
						{achievementText(t, 'T_SITE_ACHIEVEMENTS_SHELF_EMPTY')}
					</Text>
				</View>
			)}

			<Text
				numberOfLines={2}
				onLayout={event =>
					setFooterWrapped(
						event.nativeEvent.layout.height > 12 * FOOTER_LINE * scale * 1.5 ||
							// Once moved up its lines are tighter, so keep it there while it still wraps.
							(footerWrapped &&
								event.nativeEvent.layout.height > 12 * FOOTER_WRAPPED_LINE * scale * 1.5),
					)
				}
				style={[styles.footer, footerWrapped && styles.footerWrapped]}
			>
				{achievementText(
					t,
					count >= maxSelected
						? 'T_SITE_ACHIEVEMENTS_SHELF_FULL'
						: 'T_SITE_ACHIEVEMENTS_SHELF_HINT',
				)}
			</Text>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		shelf: {
			width: SHELF_WIDTH * scale,
			height: SHELF_HEIGHT * scale,
			borderRadius: RADIUS_CARD * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelOrange,
			backgroundColor: achievementsColors.surface,
		},
		heading: {
			position: 'absolute',
			left: INNER_X * scale,
			top: HEADING_Y * scale,
			width: INNER_WIDTH * scale,
			height: HEADING_HEIGHT * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: 8 * scale,
		},
		// Figma "Button/Label": Sofia Sans Bold 14, line-height 14, letter-spacing 0.0547.
		headingLabel: {
			flex: 1,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
			lineHeight: 14 * scale,
			letterSpacing: 0.0547 * scale,
		},
		headingCount: {
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
			lineHeight: 14 * scale,
			letterSpacing: 0.0547 * scale,
			textAlign: 'right',
		},
		tiles: {
			position: 'absolute',
			left: INNER_X * scale,
			top: TILES_Y * scale,
			width: INNER_WIDTH * scale,
			height: TILE_HEIGHT * scale,
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		emptyPlate: {
			position: 'absolute',
			left: INNER_X * scale,
			top: TILES_Y * scale,
			width: INNER_WIDTH * scale,
			height: TILE_HEIGHT * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_CARD * scale,
			backgroundColor: achievementsColors.emptyShelfPlate,
		},
		emptyText: {
			width: EMPTY_TEXT_WIDTH * scale,
			color: achievementsColors.muted,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
			lineHeight: 14 * scale,
			letterSpacing: 0.0547 * scale,
			textAlign: 'center',
		},
		// Sofia Sans Medium 12, line-height 1.2.
		footer: {
			position: 'absolute',
			left: INNER_X * scale,
			top: FOOTER_Y * scale,
			width: INNER_WIDTH * scale,
			color: achievementsColors.secondary,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
			lineHeight: 12 * FOOTER_LINE * scale,
		},
		footerWrapped: {
			top: FOOTER_WRAPPED_Y * scale,
			lineHeight: 12 * FOOTER_WRAPPED_LINE * scale,
		},
	});

export default SelectedForProfile;
